/* eslint-disable no-unsafe-optional-chaining -- needed */

import {http} from '@/services/http'
import type {
	GeoResults,
	MapEntity,
	VetClinicsOutput,
	VetApiResponse,
	VeevaEntity,
} from '@/services/vet-clinics/models'

async function geocode(address: string): Promise<GeoResults> {
	try {
		const {data} = await http({
			method: 'POST',
			url: '/api/google/geocode',
			data: {address},
		})
		return data as GeoResults
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any needed
	} catch (error: any) {
		throw new Error(
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- needed
			`Geocode API error status ${error.response.status}\nMessage: ${error.message}`
		)
	}
}

async function search(
	classification: string,
	q = '*',
	offset = 0,
	lat = '',
	lng = '',
	dist = '10mi'
): Promise<VetApiResponse> {
	try {
		const requestBody = {
			q,
			offset,
			classification,
			lat,
			lng,
			dist,
		}

		const {data: apiResponse} = await http.post<VetClinicsOutput>(
			'/api/vet',
			requestBody
		)

		// Handle error responses
		if (!apiResponse.success) {
			throw new Error(apiResponse.message || 'API request failed')
		}

		// The API returns entities in data.entities - transform them to MapEntity format
		const veevaEntities = apiResponse.data.entities || []
		const entities: MapEntity[] = veevaEntities.map(
			(veevaEntity: VeevaEntity) => ({
				entityId: veevaEntity.entityId,
				entity: veevaEntity.entity,
			})
		)

		return {entities}
	} catch (e) {
		throw new Error("Couldn't fetch data")
	}
}

async function searchLatLng(
	lat: string,
	lng: string,
	classification: string
): Promise<VetApiResponse> {
	return search(classification, '*', 0, lat, lng)
}

export const fetchVetClinics = async (
	address: string
): Promise<MapEntity[]> => {
	if (!address) {
		return []
	}

	try {
		const geo = await geocode(address)
		if (geo.status !== 'OK') {
			return []
		}
		// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- works for now
		const {lat, lng} = geo?.results[0]?.geometry?.location!

		const response = await searchLatLng(
			String(lat),
			String(lng),
			'ELA_AC_061'
		)
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- needed
		if (response?.entities !== undefined) {
			return response.entities
		}
		return []
	} catch (error) {
		// Return empty array on error to prevent UI from breaking
		return []
	}
}

export const fetchShelters = async (address: string): Promise<MapEntity[]> => {
	if (!address) {
		return []
	}

	try {
		const geo = await geocode(address)
		if (geo.status !== 'OK') {
			return []
		}
		// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- works for now
		const {lat, lng} = geo?.results[0]?.geometry?.location!

		const response = await searchLatLng(
			String(lat),
			String(lng),
			'ELA_AC_003'
		)
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- no other way
		if (response?.entities !== undefined) {
			return response.entities
		}
		return []
	} catch (error) {
		// Return empty array on error to prevent UI from breaking
		return []
	}
}

export {search, searchLatLng}
